import React from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PMSOfferings = () => {
  const navigate = useNavigate();

  const offerings = [
    {
      title: "SPARK",
      subtitle: "Emerging Quality-Growth",
      description: "Lesser known small-cap companies with strong fundamentals for long-term growth.",
      link: "/spark-pms-smallcap-5000-cr-cos"
    },
    {
      title: "GEM",
      subtitle: "Quality-Momentum Strategy",
      description: "Companies with strong price trends and growth momentum across all market caps.",
      link: "/gem-pms-quality-momentum"
    },
    {
      title: "JEWEL",
      subtitle: "Flexi Cap Quality-Growth",
      description: "Balanced investment in large, mid, and a few small-cap companies for long-term growth.",
      link: "/jewel-pms-large-midcap-focused"
    },
    {
      title: "EMERALD",
      isEmeraldCombined: true,
      emeraldE: {
        label: "E",
        subtitle: "Diversified Equity MF",
        description: "Long-term Growth",
        link: "/pms/emerald-equity"
      },
      emeraldNE: {
        label: "NE",
        subtitle: "Strategic Debt & Other",
        description: "Capital Preservation & Liquidity",
        link: "/pms/emerald-non-equity"
      }
    },
    {
      title: "DIAMOND",
      subtitle: "Retirement Planning",
      description: "Provides stable retirement income through diversified debt, equity, and alternative investments for sustainability.",
      link: "/pms/diamond"
    },
    {
      title: "CORAL",
      subtitle: "Retirement Planning",
      description: "Provides stable retirement income through diversified debt, equity, and alternative investments for sustainability.",
      link: "/pms/coral"
    }
  ];

  const handleKnowMore = (link) => {
    navigate(link);
  };

  const handleBackClick = () => {
    // Navigate back to main page
    navigate('/wealth-services/portfolio-management');

    // Use a small timeout to ensure navigation completes before trying to open dropdown
    setTimeout(() => {
      // Custom event to communicate with navbar component
      const event = new CustomEvent('openNavDropdown', {
        detail: {
          section: 'wealthManagement', // The Services section
          subsection: 0  // Index of "Investment Advisory" in the items array
        }
      });
      window.dispatchEvent(event);
    }, 100);
  };

  const KnowMoreButton = ({ link }) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleKnowMore(link);
      }}
      className="relative mt-4 group inline-flex items-center justify-center rounded-full bg-blue-50 px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium text-blue-900 hover:bg-blue-100 transition-all duration-300"
    >
      Know More
      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-300" />
    </button>
  );

  return (
    <div id="pms-offerings" className="relative bg-gradient-to-b from-gray-50 to-white">
      <div className="relative max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Improved header section with left-aligned back button */}
        <div className="pt-16 mb-8 md:mb-16 relative">
          {/* Back Button - now positioned to the left */}
          <div className="absolute sm:top-16 top-16 lg:top-20">
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 text-orange-400 hover:text-orange-600 transition-all duration-300 font-medium text-sm mb-6 opacity-60 hover:opacity-100"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
              <span className="hidden sm:inline">Back to Wealth Management</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>
          
          {/* Title section */}
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4 pt-12">Our PMS Offerings</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              All investment selections are based on first principles leveraging our investment philosophy and data science models.
            </p>
          </div>
        </div>

        {/* Responsive card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {offerings.map((offering, index) => (
            offering.isEmeraldCombined ? (
              <div
                key={index}
                className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-md hover:shadow-xl transition-all duration-300 relative group overflow-hidden flex flex-col border border-gray-100 min-h-[290px] sm:min-h-[370px] h-full"
              >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 -mr-12 -mt-12 bg-blue-50 rounded-full opacity-20 transform rotate-45"></div>
                <div className="absolute bottom-0 left-0 w-16 sm:w-24 h-16 sm:h-24 -ml-8 -mb-8 bg-blue-50 rounded-full opacity-20"></div>

                <div className="relative flex-grow flex flex-col h-full">
                  <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-1 sm:mb-2">{offering.title}</h3>
                  <div className="flex flex-col divide-y divide-blue-100 bg-blue-50/30 rounded-xl">
                    {/* Emerald E */}
                    <div className="flex items-start gap-3 py-2">
                      <span className="inline-block bg-blue-600 text-white text-xs font-bold rounded px-2 py-0.5 mr-2 w-8 text-center mt-1">{offering.emeraldE.label}</span>
                      <div className="flex flex-col items-start">
                        <p className="text-blue-700 font-semibold text-base sm:text-lg mb-0.5 leading-tight">{offering.emeraldE.subtitle}</p>
                        <p className="text-gray-700 text-sm mb-1 leading-tight">{offering.emeraldE.description}</p>
                        <div className="mt-2"><KnowMoreButton link={offering.emeraldE.link} /></div>
                      </div>
                    </div>
                    {/* Emerald NE */}
                    <div className="flex items-start gap-3 py-2">
                      <span className="inline-block bg-orange-500 text-white text-xs font-bold rounded px-2 py-0.5 mr-2 w-8 text-center mt-1">{offering.emeraldNE.label}</span>
                      <div className="flex flex-col items-start">
                        <p className="text-blue-700 font-semibold text-base sm:text-lg mb-0.5 leading-tight">{offering.emeraldNE.subtitle}</p>
                        <p className="text-gray-700 text-sm mb-1 leading-tight">{offering.emeraldNE.description}</p>
                        <div className="mt-2"><KnowMoreButton link={offering.emeraldNE.link} /></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                key={index}
                className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-md hover:shadow-xl transition-all duration-300 relative group overflow-hidden flex flex-col border border-gray-100 cursor-pointer min-h-[290px] sm:min-h-[370px] h-full"
                onClick={() => handleKnowMore(offering.link)}
              >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 -mr-12 -mt-12 bg-blue-50 rounded-full opacity-20 transform rotate-45"></div>
                <div className="absolute bottom-0 left-0 w-16 sm:w-24 h-16 sm:h-24 -ml-8 -mb-8 bg-blue-50 rounded-full opacity-20"></div>

                <div className="relative flex-grow">
                  <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-1 sm:mb-2">{offering.title}</h3>
                  <p className="text-blue-600 font-medium mb-2 sm:mb-4 text-base sm:text-lg">{offering.subtitle}</p>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">{offering.description}</p>
                </div>

                <KnowMoreButton link={offering.link} />
              </div>
            )
          ))}
        </div>
      </div>
    </div>
  );
};

export default PMSOfferings;